import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';
import type { OrgCustomRole } from 'src/types/org.types';

interface InviteMembersModalProps {
  isDemo: boolean;
  orgId: string | undefined;
  customRoles: OrgCustomRole[];
  onClose: () => void;
  onSubmit: (email: string, roleId: string | undefined, customRoleId: string | undefined) => Promise<void>;
}

export const InviteMembersModal: React.FC<InviteMembersModalProps> = ({
  isDemo,
  orgId,
  customRoles,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    roleId: undefined as string | undefined,
    customRoleIds: [] as string[]
  });
  const [isSaving, setIsSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const isValidEmail = (email: string) => {
    return email.includes('@') && email.includes('.');
  };

  const handleSubmit = async () => {
    if (!orgId) return;

    setIsSaving(true);
    setModalError(null);

    try {
      await onSubmit(
        formData.email.trim(),
        formData.roleId,
        formData.customRoleIds.length > 0 ? formData.customRoleIds[0] : undefined
      );
      onClose();
      setFormData({ email: '', roleId: undefined, customRoleIds: [] });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send invite';
      setModalError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setModalError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">Invite Members</h2>
          <button onClick={handleClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">Email Address *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="colleague@company.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">Custom Role (Optional)</label>
            <select
              value={formData.customRoleIds[0] || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, customRoleIds: e.target.value ? [e.target.value] : [] }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white/30"
            >
              <option value="">No custom role (Member)</option>
              {customRoles.map(role => (
                <option key={role.customRoleId} value={role.customRoleId}>
                  {role.name}
                </option>
              ))}
            </select>
            <p className="text-white/40 text-xs mt-1">
              User will receive Member role by default if no custom role selected
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleClose} variant="ghost" className="flex-1">Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="default"
            className="flex-1"
            disabled={!isValidEmail(formData.email) || isSaving}
          >
            {isSaving ? 'Sending...' : 'Send Invite'}
          </Button>
        </div>
      </div>
    </div>
  );
};
